/* eslint-disable jsx-a11y/accessible-emoji */
import { gql } from '@apollo/client';
import { Box, Button, Icons, Tabs, Text } from '@mnd-frontend/ui/publish/components';
import { GREY_900 } from '@mnd-frontend/ui/shared/tokens';
import { getCookie } from '@mnd-frontend/utils';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useCurrentPressroom } from '../../../../contexts/CurrentPressroomContext';
import { useQueryState } from '../../../../contexts/UseQueryStateContext';
import { useDebugInfoQuery } from '../../../../generated/hooks';
import CookieOverrides from './CookieOverrides';
import CookieOverview from './CookieOverview';
import QuickNavigate from './QuickNavigate';
import SupportForm from './SupportForm';

gql`
  query DebugInfo($pressroomId: ID!) {
    me {
      id
      viewAsAdmin
      accessLevel(pressroomId: $pressroomId)
    }
  }
`;

const BottomLeftCorner = styled.div<{ $fixedPosition: boolean }>`
  height: 56px;
  ${props =>
    props.$fixedPosition &&
    css`
      bottom: 0;
      left: 0;
      margin: 1rem;
      padding: 0rem;
      position: fixed;
      z-index: 1999;
    `}

  > button {
    padding: 0 1rem;
    margin: 0 auto;
    &:hover {
      color: ${GREY_900};
    }
    > div {
      justify-content: flex-start;
    }
  }
`;

type DebugTabType = 'support-form' | 'cookie-overrides' | 'quick-navigate' | 'copilot';

const DebugInfo = ({ fixedPosition = true }: { fixedPosition?: boolean }) => {
  const { pressroomId } = useCurrentPressroom();

  const [debugTab, setDebugTab] = useQueryState<DebugTabType | undefined>('debug-info', undefined);

  const { data } = useDebugInfoQuery({ variables: { pressroomId } });

  const version = getCookie('frontend_version');
  const locale = getCookie('locale');
  const hasCookies = Boolean(locale || version || getCookie('feature_toggles'));
  const viewAsAdmin = Boolean(data?.me?.viewAsAdmin);
  const accessLevel = data?.me?.accessLevel;
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentRefState, setContentRefState] = useState(contentRef.current);
  const onClose = () => setDebugTab(undefined);

  useEffect(() => {
    setContentRefState(contentRef.current);
  });

  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 't') {
        setDebugTab('quick-navigate');
      }
    };
    document.addEventListener('keydown', handle);

    return () => document.removeEventListener('keydown', handle);
  }, [setDebugTab]);

  const tabMapping: DebugTabType[] = [
    'support-form',
    'cookie-overrides',
    'quick-navigate',
    'copilot',
  ];

  return (
    <>
      <BottomLeftCorner $fixedPosition={fixedPosition}>
        <Button
          onClick={() => setDebugTab(debugTab ? undefined : 'support-form')}
          type="button"
          configuration="text"
          leadingIcon={
            hasCookies ? (
              <Icons.DebugCookie $color={viewAsAdmin ? 'RED_700' : 'GREY_900'} />
            ) : (
              <Icons.Debug $color={viewAsAdmin ? 'RED_700' : 'GREY_900'} />
            )
          }
        >
          Debug
        </Button>
      </BottomLeftCorner>
      <Box
        openedAsModal={Boolean(debugTab)}
        size="lg"
        onClose={onClose}
        bodyPadding
        bodyMinHeight="50vh"
        headerBottomBorder={false}
        headerContent={
          <>
            <Text as="h3" variant="headlineMedium" marginBottom="SPACING_1">
              Debug Info
            </Text>
            <CookieOverview
              show={true}
              version={version}
              locale={locale}
              viewAsAdmin={viewAsAdmin}
              accessLevel={accessLevel}
            />
            <Tabs
              variant="mainTabs"
              activeTab={tabMapping.indexOf(debugTab ?? 'support-form')}
              setActiveTab={tab => setDebugTab(tabMapping[tab])}
              contentRef={contentRefState}
              noHeaderContentSpacing
              tabs={[
                {
                  title: 'Support Form',
                  renderContent: () => <SupportForm onClose={onClose} />,
                },
                {
                  title: 'Cookie Overrides',
                  renderContent: () => (
                    <CookieOverrides onClose={onClose} viewAsAdmin={viewAsAdmin} />
                  ),
                },
                {
                  title: 'Quick Navigate',
                  renderContent: () => <QuickNavigate onClose={onClose} />,
                },
              ]}
            />
          </>
        }
      >
        <div ref={contentRef} />
      </Box>
    </>
  );
};

export default DebugInfo;
